import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaEnvelope,
  FaExternalLinkAlt,
  FaHandshake,
  FaMapMarkerAlt,
  FaPhoneAlt,
} from "react-icons/fa";
import { FiGlobe } from "react-icons/fi";

import { getAllPartenaires } from "../../services/partenairesServices";
import useScrollToTop from "./../../hooks/useScollToTop";

const FALLBACK = "/img/no-image.jpg";
const API_BASE = process.env.REACT_APP_API_URL || "";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function toAbsolute(url) {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  if (cleanUrl.startsWith("/img/")) return cleanUrl;

  if (cleanUrl.startsWith("/uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}${cleanUrl}`;
  }

  if (cleanUrl.startsWith("uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}/${cleanUrl}`;
  }

  if (cleanUrl.startsWith("/")) return cleanUrl;

  return `${API_BASE.replace(/\/$/, "")}/${cleanUrl}`;
}

function sanitizeHtml(html = "") {
  return DOMPurify.sanitize(html || "", {
    USE_PROFILES: {
      html: true,
    },
    FORBID_ATTR: ["style"],
  });
}

function isActive(value) {
  if (value === undefined || value === null || value === "") return true;
  return value === true || value === 1 || value === "1";
}

function normalizePhone(phone = "") {
  return String(phone || "").replace(/\s+/g, "");
}

function LoadingState() {
  return (
    <main className="min-h-screen bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] dark:bg-slate-950">
      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="h-96 animate-pulse rounded-[2.5rem] bg-slate-200 dark:bg-slate-800" />
        <div className="mt-8 grid gap-5 lg:grid-cols-2">
          <div className="h-72 animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800" />
          <div className="h-72 animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
    </main>
  );
}

function NotFoundState() {
  const navigate = useNavigate();

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="relative mx-auto max-w-3xl px-4 pb-16 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => navigate("/partenaires")}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
        >
          <FaArrowLeft className="text-xs" />
          Retour partenaires
        </button>

        <div className="mt-10 rounded-[2rem] border border-dashed border-slate-300 bg-white/80 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
            <FaHandshake />
          </div>

          <h1 className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
            Partenaire introuvable
          </h1>

          <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
            La fiche demandée n’existe pas ou n’est plus publiée.
          </p>

          <Link
            to="/partenaires"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
          >
            Voir tous les partenaires
          </Link>
        </div>
      </div>
    </main>
  );
}

function ContactCard({ icon: Icon, label, value, href }) {
  if (!value) return null;

  const content = (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm transition hover:border-primary/30 hover:shadow-md dark:border-white/10 dark:bg-white/5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
        <Icon />
      </div>

      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
          {label}
        </p>

        <p className="mt-1 break-words text-sm font-bold leading-6 text-slate-800 dark:text-slate-100">
          {value}
        </p>
      </div>
    </div>
  );

  if (!href) return content;

  return (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noopener noreferrer" : undefined}>
      {content}
    </a>
  );
}

function ContentBlock({ image, imageAlt, html, reverse }) {
  const safeHtml = useMemo(() => sanitizeHtml(html), [html]);

  if (!image && !safeHtml) return null;

  return (
    <section
      className={cn(
        "grid gap-6 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 p-5 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90 lg:grid-cols-2 lg:p-7",
        reverse ? "lg:[&>*:first-child]:order-2" : ""
      )}
    >
      {image && (
        <div className="flex min-h-[320px] items-center justify-center overflow-hidden rounded-[2rem] bg-slate-50 dark:bg-white/5">
          <img
            src={image}
            alt={imageAlt}
            loading="lazy"
            className="h-full max-h-[520px] w-full object-contain"
            onError={(event) => {
              event.currentTarget.src = FALLBACK;
            }}
          />
        </div>
      )}

      {safeHtml && (
        <div
          className={cn(
            "prose prose-slate max-w-none self-center dark:prose-invert",
            "text-sm leading-7 text-slate-600 dark:text-slate-300",
            "prose-a:font-bold prose-a:text-primary",
            "prose-headings:font-barlow prose-headings:font-bold",
            "dark:[&_strong]:!text-white dark:[&_b]:!text-white"
          )}
          dangerouslySetInnerHTML={{
            __html: safeHtml,
          }}
        />
      )}

      <div
        aria-hidden
        className="lg:col-span-2 h-1.5 w-full rounded-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
      />
    </section>
  );
}

export default function PartenaireDetail() {
  useScrollToTop();

  const navigate = useNavigate();
  const { nom } = useParams();

  const [partenaire, setPartenaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchPartenaire = async () => {
      try {
        setLoading(true);
        setNotFound(false);

        const data = await getAllPartenaires();
        const list = Array.isArray(data) ? data : [];
        const decodedNom = decodeURIComponent(nom || "");

        const match = list.find((item) => {
          return (
            isActive(item?.is_active) &&
            String(item?.nom || "").trim() === decodedNom.trim()
          );
        });

        if (!mounted) return;

        setPartenaire(match || null);
        setNotFound(!match);
      } catch (error) {
        console.error("Erreur getAllPartenaires:", error);

        if (mounted) {
          setPartenaire(null);
          setNotFound(true);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPartenaire();

    return () => {
      mounted = false;
    };
  }, [nom]);

  const logo = useMemo(
    () => toAbsolute(partenaire?.photo_url) || FALLBACK,
    [partenaire?.photo_url]
  );

  const image2 = useMemo(
    () => toAbsolute(partenaire?.photo2_url),
    [partenaire?.photo2_url]
  );

  const image3 = useMemo(
    () => toAbsolute(partenaire?.photo3_url),
    [partenaire?.photo3_url]
  );

  const email = partenaire?.Mail || partenaire?.mail || partenaire?.email || "";
  const phone = partenaire?.tel || partenaire?.telephone || "";

  const mapUrl = useMemo(() => {
    if (!partenaire?.adresse) return "";
    return `https://www.google.com/maps?q=${encodeURIComponent(partenaire.adresse)}&output=embed`;
  }, [partenaire?.adresse]);

  const mapLink = useMemo(() => {
    if (!partenaire?.adresse) return "";
    return `https://www.google.com/maps?q=${encodeURIComponent(partenaire.adresse)}`;
  }, [partenaire?.adresse]);

  if (loading) return <LoadingState />;
  if (notFound || !partenaire) return <NotFoundState />;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate("/partenaires")}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <FaArrowLeft className="text-xs" />
            Retour partenaires
          </button>

          {partenaire.link_url && (
            <a
              href={partenaire.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
            >
              <FaExternalLinkAlt className="text-xs" />
              Site du partenaire
            </a>
          )}
        </div>

        <section className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90">
          <div className="grid lg:grid-cols-[0.42fr_0.58fr]">
            <div className="flex min-h-[320px] items-center justify-center bg-slate-50 p-8 dark:bg-white/5">
              <img
                src={logo}
                alt={partenaire.nom}
                className="max-h-56 w-full object-contain"
                loading="lazy"
                onError={(event) => {
                  event.currentTarget.src = FALLBACK;
                }}
              />
            </div>

            <div className="relative p-6 sm:p-8 lg:p-10">
              <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                  <FaHandshake />
                  Partenaire du club
                </div>

                <h1 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl">
                  {partenaire.nom}
                </h1>

                <p className="mt-4 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
                  Merci à notre partenaire pour son soutien et son engagement
                  auprès du club.
                </p>

                <div className="mt-7 grid gap-3 sm:grid-cols-2">
                  <ContactCard
                    icon={FaMapMarkerAlt}
                    label="Adresse"
                    value={partenaire.adresse}
                    href={mapLink}
                  />

                  <ContactCard
                    icon={FaPhoneAlt}
                    label="Téléphone"
                    value={phone}
                    href={phone ? `tel:${normalizePhone(phone)}` : ""}
                  />

                  <ContactCard
                    icon={FaEnvelope}
                    label="Email"
                    value={email}
                    href={email ? `mailto:${email}` : ""}
                  />

                  <ContactCard
                    icon={FiGlobe}
                    label="Site web"
                    value={partenaire.link_url}
                    href={partenaire.link_url}
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            aria-hidden
            className="h-1.5 w-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
          />
        </section>

        <div className="mt-8 space-y-8">
          <ContentBlock
            image={image2}
            imageAlt={`${partenaire.nom} - présentation`}
            html={partenaire.description2}
          />

          <ContentBlock
            image={image3}
            imageAlt={`${partenaire.nom} - informations complémentaires`}
            html={partenaire.description3}
            reverse
          />
        </div>

        {mapUrl && (
          <section className="mt-8 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 p-3 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90">
            <iframe
              title={`Carte ${partenaire.nom}`}
              src={mapUrl}
              width="100%"
              height="360"
              className="rounded-[2rem]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />

            <div
              aria-hidden
              className="mt-3 h-1.5 w-full rounded-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
            />
          </section>
        )}
      </div>
    </main>
  );
}